// src/services/api/products.ts
import { httpClient } from '@/services/http/client';
import { ApiResponse, PaginatedResponse, FrontendPaginatedResponse } from '@/services/types/api';
import { Product, ProductFilters, CreateProductData, ProductImage, ProductListItem, Review } from '@/services/types/domain';

class ProductsApi {
    private readonly basePath = '/products';

    /**
     * Obtener lista de productos con filtros y paginación
     */
    async getProducts(filters: ProductFilters = {}): Promise<FrontendPaginatedResponse<Product>> {
        const response = await httpClient.get<PaginatedResponse<Product>>(
            this.basePath,
            { params: filters }
        );

        return this.adaptBackendResponse(response.data);
    }

    /**
     * Obtener productos destacados
     */
    async getFeaturedProducts(limit: number = 12): Promise<Product[]> {
        const response = await httpClient.get<ApiResponse<Product[]>>(
            `${this.basePath}/featured`,
            { params: { per_page: limit } }
        );
        return response.data.data;
    }

    /**
     * Buscar productos
     */
    async searchProducts(
        query: string,
        filters: Omit<ProductFilters, 'search'> = {}
    ): Promise<FrontendPaginatedResponse<Product>> {
        const response = await httpClient.get<PaginatedResponse<Product>>(
            `${this.basePath}/search`,
            { params: { q: query, ...filters } }
        );

        return this.adaptBackendResponse(response.data);
    }

    /**
     * Obtener producto por ID
     */
    async getProductById(id: number): Promise<Product | any> {
        const response = await httpClient.get<ApiResponse<Product>>(
            `${this.basePath}/${id}`
        );
        return response.data.data;
    }

    /**
     * Obtener producto por slug
     */
    async getProductBySlug(slug: string): Promise<Product> {
        const response = await httpClient.get<ApiResponse<Product>>(
            `${this.basePath}/${slug}`
        );
        //console.log("a ver", response.data.data);
        return response.data.data;
    }

    /**
     * Obtener productos de una categoría
     */
    async getProductsByCategory(
        categoryId: number,
        filters: Omit<ProductFilters, 'category_id'> = {}
    ): Promise<FrontendPaginatedResponse<ProductListItem>> {
        const response = await httpClient.get<PaginatedResponse<ProductListItem>>(
            `${this.basePath}/category/${categoryId}`,
            { params: filters }
        );

        return this.adaptBackendResponse(response.data);
    }

    /**
     * Obtener productos relacionados
     */
    async getRelatedProducts(productId: number, limit: number = 8): Promise<Product[]> {
        const response = await httpClient.get<ApiResponse<Product[]>>(
            `${this.basePath}/${productId}/related`,
            { params: { limit } }
        );
        return response.data.data;
    }

    /**
     * Crear nuevo producto (solo vendedores)
     */
    async createProduct(productData: CreateProductData): Promise<Product> {
        const response = await httpClient.post<ApiResponse<Product>>(
            this.basePath,
            productData
        );
        return response.data.data;
    }

    /**
     * Actualizar producto existente
     */
    async updateProduct(productId: number, productData: Partial<CreateProductData>): Promise<Product> {
        const response = await httpClient.put<ApiResponse<Product>>(
            `${this.basePath}/${productId}`,
            productData
        );
        return response.data.data;
    }

    /**
     * Eliminar producto
     */
    async deleteProduct(productId: number): Promise<void> {
        await httpClient.delete(`${this.basePath}/${productId}`);
    }

    /**
     * Subir imágenes de producto
     */
    async uploadProductImages(productId: number, images: File[]): Promise<ProductImage[]> {
        const formData = new FormData();
        images.forEach((image, index) => {
            formData.append(`images[${index}]`, image);
        });

        const response = await httpClient.upload<ApiResponse<ProductImage[]>>(
            `${this.basePath}/${productId}/images`,
            formData
        );

        return response.data.data;
    }

    /**
     * Eliminar imagen de producto
     */
    async deleteProductImage(productId: number, imageId: number): Promise<void> {
        await httpClient.delete(`${this.basePath}/${productId}/images/${imageId}`);
    }

    /**
     * Verificar disponibilidad de stock
     */
    async checkStock(productId: number, variantId?: number): Promise<{
        available: boolean;
        stock_quantity: number;
    }> {
        const params = variantId ? { variant_id: variantId } : {};
        const response = await httpClient.get<ApiResponse<{
            available: boolean;
            stock_quantity: number;
        }>>(
            `${this.basePath}/${productId}/stock`,
            { params }
        );
        return response.data.data;
    }

    /**
     * Obtener reseñas de un producto
     */
    async getProductReviews(
        productId: number,
        page: number = 1,
        perPage: number = 10
    ): Promise<FrontendPaginatedResponse<Review>> {
        const response = await httpClient.get<PaginatedResponse<Review>>(
            `${this.basePath}/${productId}/reviews`,
            { params: { page, per_page: perPage } }
        );

        return this.adaptBackendResponse(response.data);
    }

    /**
     * Obtener estadísticas de reseñas
     */
    async getProductReviewStats(productId: number): Promise<{
        average_rating: number;
        total_reviews: number;
        rating_distribution: Record<string, number>;
    }> {
        const response = await httpClient.get<ApiResponse<{
            average_rating: number;
            total_reviews: number;
            rating_distribution: Record<string, number>;
        }>>(
            `${this.basePath}/${productId}/reviews/stats`
        );
        return response.data.data;
    }

    /**
     * Crear reseña (requiere autenticación)
     */
    async createReview(productId: number, data: {
        rating: number;
        title?: string;
        comment?: string;
        order_item_id?: number;
    }): Promise<Review> {
        const response = await httpClient.post<ApiResponse<Review>>(
            `${this.basePath}/${productId}/reviews`,
            data
        );
        return response.data.data;
    }

    /**
     * Marcar reseña como útil
     */
    async markReviewHelpful(reviewId: number): Promise<void> {
        await httpClient.post(`/reviews/${reviewId}/helpful`);
    }

    /**
     * Obtener productos del vendedor (mis productos)
     */
    async getMyProducts(filters: ProductFilters = {}): Promise<FrontendPaginatedResponse<Product>> {
        const response = await httpClient.get<PaginatedResponse<Product>>(
            '/seller/products',
            { params: filters }
        );

        return this.adaptBackendResponse(response.data);
    }
    /**
     * Manejar Stock del producto
     */
    async updateProductStock(productId: number, data: { quantity_change: number; type: string; notes: string }): Promise<Product> {
        const response = await httpClient.post<ApiResponse<Product>>(
            `/seller/products/${productId}/stock`,
            data
        );
        return response.data.data;
    }
    /**
     * Generar SKU único
     */
    generateSKU(productName: string, categoryId: number): string {
        const cleanName = productName
            .replace(/[^a-zA-Z0-9]/g, '')
            .substring(0, 6)
            .toUpperCase();
        const timestamp = Date.now().toString().slice(-6);
        return `${cleanName}-${categoryId}-${timestamp}`;
    }

    /**
     * Validar imágenes
     */
    validateImages(files: FileList | File[]): { valid: boolean; errors: string[] } {
        const errors: string[] = [];
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const maxFiles = 10;

        const fileArray = Array.from(files);

        if (fileArray.length > maxFiles) {
            errors.push(`Máximo ${maxFiles} imágenes permitidas`);
        }

        fileArray.forEach((file, index) => {
            if (!allowedTypes.includes(file.type)) {
                errors.push(`Imagen ${index + 1}: Tipo no permitido. Use JPG, PNG o WebP`);
            }

            if (file.size > maxSize) {
                errors.push(`Imagen ${index + 1}: Tamaño máximo 5MB`);
            }
        });

        return {
            valid: errors.length === 0,
            errors
        };
    }

    /**
     * Adaptar respuesta paginada del backend al formato frontend
     */
    private adaptBackendResponse<T>(backendResponse: PaginatedResponse<T>): FrontendPaginatedResponse<T> {
        const { data, pagination } = backendResponse;

        return {
            data,
            meta: {
                current_page: pagination.current_page,
                from: (pagination.current_page - 1) * pagination.per_page + 1,
                last_page: pagination.total_pages,
                per_page: pagination.per_page,
                to: Math.min(pagination.current_page * pagination.per_page, pagination.total),
                total: pagination.total,
            },
            links: {
                first: `?page=1`,
                last: `?page=${pagination.total_pages}`,
                prev: pagination.current_page > 1 ? `?page=${pagination.current_page - 1}` : undefined,
                next: pagination.has_more ? `?page=${pagination.current_page + 1}` : undefined,
            },
        };
    }
}

// Instancia singleton
export const productsApi = new ProductsApi();